/**
 * Derives a room's architectural trim from its built geometry: boxes in, more boxes out.
 *
 * The house is otherwise flat-shaded cuboids, which is why it reads as painted rather
 * than built. Trim is the cheapest fix — a band standing a couple of centimetres proud
 * of a wall catches the light differently from the wall behind it, and suddenly the
 * surface has a near edge and a far one.
 *
 * Everything here is decoration. Trim is deliberately absent from the collider set the
 * renderer builds (see `LevelGeometry`), so no amount of moulding can change where the
 * player may walk — the "never obstructs the player" requirement holds by construction
 * rather than by tuning.
 *
 * Pure and headless, like the rest of `src/game/`: no three.js, no Rapier, no React.
 */

import { mulberry32, seedFromString } from '../../utils/seededRandom';
import {
  WALL_THICKNESS,
  type BuiltLevel,
  type DoorFace,
  type RoomWall,
  type WindowFace,
} from './buildGeometry';
import type { Box, GroundPoint } from './types';

/**
 * Trim is deliberately oversized — roughly twice what a real house uses.
 *
 * A real skirting board projects about 15 mm and reads because photorealistic rendering
 * gives it a contact shadow and a soft gradient. This game has neither: `MeshToonMaterial`
 * quantises light into three hard steps, so a face pointing the same way as the wall
 * behind it comes out the *identical* colour no matter how far it sticks out. All the
 * relief lives in the small faces that point somewhere else — the top of a skirting, the
 * underside of a cornice — and those only read if they are big enough to see from across
 * a room. Chunky simplified shapes are what the visual-style spec asks for anyway.
 */
export const SKIRTING_HEIGHT = 0.22;
export const CORNICE_HEIGHT = 0.18;

/**
 * Where the wainscot's cap rail tops out — chest height on a standing adult, which is
 * where a dado rail goes in a real room. Fixed in metres and deliberately not a fraction
 * of the wall: on a 4.68 m wall a proportional rail would sit above head height and read
 * as a stripe rather than as panelling.
 */
export const WAINSCOT_HEIGHT = 1.05;
export const WAINSCOT_CAP_HEIGHT = 0.07;

/**
 * Below this the wall does not get panelling. A room of ordinary height is already
 * broken up by its skirting and cornice; adding a third horizontal band would crowd it.
 * Panelling is for walls tall enough that the middle is otherwise empty.
 */
export const WAINSCOT_MIN_WALL_HEIGHT = 3.2;

/** Stiles land roughly this far apart, nudged so a wall ends on a whole panel. */
const WAINSCOT_PANEL_TARGET = 0.85;

/** How far each band stands out from the wall face. See the note above on sizing. */
const SKIRTING_PROUD = 0.06;
const CORNICE_PROUD = 0.1;
const WAINSCOT_CAP_PROUD = 0.07;
const WAINSCOT_STILE_PROUD = 0.03;
/** Panel dividers are narrow uprights, not bands — this is their width along the wall. */
const WAINSCOT_STILE_WIDTH = 0.07;

/**
 * How wide the band around a doorway is, and how far it stands out. The casing is laid
 * *outside* the opening on all three sides — never across it — so framing a doorway
 * cannot narrow it. That is the "never obstructs the player" requirement in arithmetic.
 */
const CASING_WIDTH = 0.12;
const CASING_PROUD = 0.06;

/** A window's surround, and the shelf under it. Sills stick out further than casings. */
const WINDOW_FRAME_WIDTH = 0.11;
const WINDOW_FRAME_PROUD = 0.05;
const SILL_DEPTH = 0.14;
const SILL_THICKNESS = 0.06;

/**
 * Glazing bars: the grid that turns one sheet of glass into panes.
 *
 * The pane size is what is held roughly constant, not the number of bars — a window twice
 * as tall gets twice as many cross bars rather than two enormous panes. That is how real
 * joinery works, and it is also what keeps a tall opening from reading as a hole.
 */
const BAR_TARGET_PANE = 0.75;
const BAR_WIDTH = 0.06;
/** Barely proud: bars sit against the glass, not on a shelf of their own. */
const BAR_PROUD = 0.025;

/**
 * How far a band reaches back *into* the wall. Without it the band's back face and the
 * wall face would be coplanar and z-fight; with it there is no shared plane at all.
 */
const WALL_OVERLAP = 0.01;

/**
 * Runs stop this far short at each end. Two walls' boxes already overlap at a corner —
 * invisible while both are buried in solid geometry, but a crossing pair of skirtings
 * standing proud would not be. Inset by a wall thickness and they butt instead.
 */
const CORNER_INSET = WALL_THICKNESS;

export type TrimRole =
  | 'skirting'
  | 'cornice'
  | 'wainscot-cap'
  | 'wainscot-stile'
  | 'casing'
  | 'window-frame'
  | 'window-sill'
  | 'window-bar';

export type TrimPiece = {
  role: TrimRole;
  roomId: string;
  box: Box;
  color: string;
};

/** The axis a wall's face points along; the wall runs along the other one. */
function normalAxis(inward: GroundPoint): 'x' | 'z' {
  return inward.x !== 0 ? 'x' : 'z';
}

/** The stretch of a wall a piece covers, after both ends are inset for the corners. */
type Run = { center: number; size: number };

function wallRun(wall: RoomWall): Run | null {
  const along = normalAxis(wall.inward) === 'x' ? 'z' : 'x';
  const size = wall.box.size[along] - 2 * CORNER_INSET;
  return size > 0 ? { center: wall.box.center[along], size } : null;
}

/**
 * One piece of trim on a wall, standing proud of its inward face. `run` says how much of
 * the wall's length it covers — the whole thing for a band, a sliver for an upright.
 */
function trimPiece(
  wall: RoomWall,
  role: TrimRole,
  bottom: number,
  height: number,
  proud: number,
  run: Run,
  color: string,
): TrimPiece {
  const normal = normalAxis(wall.inward);
  const sign = normal === 'x' ? wall.inward.x : wall.inward.z;
  const face = wall.box.center[normal] + (sign * wall.box.size[normal]) / 2;
  const depth = proud + WALL_OVERLAP;
  const center = face + (sign * (proud - WALL_OVERLAP)) / 2;

  return {
    role,
    roomId: wall.roomId,
    color,
    box: {
      center: {
        x: normal === 'x' ? center : run.center,
        y: bottom + height / 2,
        z: normal === 'x' ? run.center : center,
      },
      size: {
        x: normal === 'x' ? depth : run.size,
        y: height,
        z: normal === 'x' ? run.size : depth,
      },
    },
  };
}

/**
 * Panelling for a wall tall enough to need its middle broken up: a cap rail at chest
 * height with uprights dividing the field below it into panels.
 *
 * Panel width is seeded from the room id, so two rooms panel slightly differently while
 * either one renders identically on every build (architectural rule 4).
 */
function wainscot(wall: RoomWall, run: Run, color: string): TrimPiece[] {
  const random = mulberry32(seedFromString(`wainscot:${wall.roomId}`));
  const target = WAINSCOT_PANEL_TARGET * (0.88 + random() * 0.24);
  const panels = Math.max(1, Math.round(run.size / target));

  const capBottom = WAINSCOT_HEIGHT - WAINSCOT_CAP_HEIGHT;
  const pieces: TrimPiece[] = [
    trimPiece(wall, 'wainscot-cap', capBottom, WAINSCOT_CAP_HEIGHT, WAINSCOT_CAP_PROUD, run, color),
  ];

  const from = run.center - run.size / 2;
  const spacing = run.size / panels;
  for (let stile = 0; stile <= panels; stile++) {
    pieces.push(
      trimPiece(
        wall,
        'wainscot-stile',
        SKIRTING_HEIGHT,
        capBottom - SKIRTING_HEIGHT,
        WAINSCOT_STILE_PROUD,
        { center: from + stile * spacing, size: WAINSCOT_STILE_WIDTH },
        color,
      ),
    );
  }

  return pieces;
}

/**
 * The frame around one room's side of a doorway: a head across the top and a jamb down
 * each side. All three sit clear of the opening itself, so the gap the player walks
 * through is exactly as wide and as tall as it was without them.
 */
function casing(face: DoorFace, color: string): TrimPiece[] {
  // A door face has the same geometry as a wall for these purposes: a surface with an
  // inward normal, and a length to lay pieces along.
  const wall: RoomWall = {
    roomId: face.roomId,
    // A door face is recorded at the wall's centre plane, so give it the wall's
    // thickness and `trimPiece` finds the same visible surface it would for a wall.
    box: { center: face.center, size: { x: WALL_THICKNESS, y: 0, z: WALL_THICKNESS } },
    color,
    pattern: 'paint',
    isFullHeight: false,
    inward: face.inward,
  };
  const along = normalAxis(face.inward) === 'x' ? 'z' : 'x';
  const middle = face.center[along];
  const jambCenter = face.width / 2 + CASING_WIDTH / 2;

  return [
    trimPiece(
      wall,
      'casing',
      face.height,
      CASING_WIDTH,
      CASING_PROUD,
      { center: middle, size: face.width + 2 * CASING_WIDTH },
      color,
    ),
    ...[-1, 1].map((side) =>
      trimPiece(
        wall,
        'casing',
        0,
        face.height + CASING_WIDTH,
        CASING_PROUD,
        { center: middle + side * jambCenter, size: CASING_WIDTH },
        color,
      ),
    ),
  ];
}

/**
 * A window's surround: frame down both sides and across the head, and a sill below it
 * projecting into the room like a shelf. As with a door casing, nothing crosses the
 * opening — a window the player cannot see out of would be a wall.
 */
function windowSurround(face: WindowFace, color: string): TrimPiece[] {
  const wall: RoomWall = {
    roomId: face.roomId,
    box: { center: face.center, size: { x: WALL_THICKNESS, y: 0, z: WALL_THICKNESS } },
    color,
    pattern: 'paint',
    isFullHeight: false,
    inward: face.inward,
  };
  const along = normalAxis(face.inward) === 'x' ? 'z' : 'x';
  const middle = face.center[along];
  const jambCentre = face.width / 2 + WINDOW_FRAME_WIDTH / 2;
  const outerWidth = face.width + 2 * WINDOW_FRAME_WIDTH;

  return [
    // Head.
    trimPiece(
      wall,
      'window-frame',
      face.head,
      WINDOW_FRAME_WIDTH,
      WINDOW_FRAME_PROUD,
      { center: middle, size: outerWidth },
      color,
    ),
    // Jambs, stopping at the sill rather than running to the floor.
    ...[-1, 1].map((side) =>
      trimPiece(
        wall,
        'window-frame',
        face.sill,
        face.head - face.sill,
        WINDOW_FRAME_PROUD,
        { center: middle + side * jambCentre, size: WINDOW_FRAME_WIDTH },
        color,
      ),
    ),
    // Sill: a deeper shelf, so its top face catches the light coming in.
    trimPiece(
      wall,
      'window-sill',
      face.sill - SILL_THICKNESS,
      SILL_THICKNESS,
      SILL_DEPTH,
      { center: middle, size: outerWidth + WINDOW_FRAME_WIDTH },
      color,
    ),
    ...glazingBars(face, wall, middle, color),
  ];
}

/** How many bars divide a span into panes of roughly `BAR_TARGET_PANE`. */
function barCount(span: number): number {
  return Math.max(0, Math.round(span / BAR_TARGET_PANE) - 1);
}

/** Evenly spaced fractions across a span: 1/(n+1) … n/(n+1). */
function barFractions(count: number): number[] {
  return Array.from({ length: count }, (_, index) => (index + 1) / (count + 1));
}

/**
 * The bars themselves: uprights running sill to head, cross bars running jamb to jamb.
 *
 * Unlike the frame and the casing, these deliberately *do* cross the opening — that is
 * the whole point of them. They stay thin so the window is still something to look
 * through, and they carry no collider: the glass behind them (see `windowGlassBoxes`) is
 * what a thrown box hits.
 */
function glazingBars(face: WindowFace, wall: RoomWall, middle: number, color: string): TrimPiece[] {
  const height = face.head - face.sill;
  const left = middle - face.width / 2;

  const uprights = barFractions(barCount(face.width)).map((fraction) =>
    trimPiece(
      wall,
      'window-bar',
      face.sill,
      height,
      BAR_PROUD,
      { center: left + fraction * face.width, size: BAR_WIDTH },
      color,
    ),
  );

  const crossBars = barFractions(barCount(height)).map((fraction) =>
    trimPiece(
      wall,
      'window-bar',
      face.sill + fraction * height - BAR_WIDTH / 2,
      BAR_WIDTH,
      BAR_PROUD,
      { center: middle, size: face.width },
      color,
    ),
  );

  return [...uprights, ...crossBars];
}

/**
 * Skirting at the floor and cornice at the ceiling of every full-height wall. Both are
 * fixed sizes in metres, so a tall room gets the same ankle-high skirting as a low one
 * instead of a band scaled to look right only at one ceiling height. Lintels and other
 * partial-height offcuts get nothing — a doorhead with a skirting board would be absurd.
 */
export function buildTrim(built: BuiltLevel): TrimPiece[] {
  const color = built.trimColor;
  const pieces: TrimPiece[] = [];

  for (const wall of built.walls) {
    if (!wall.isFullHeight) continue;
    const run = wallRun(wall);
    if (!run) continue;

    const height = wall.box.size.y;
    const top = wall.box.center.y + height / 2;

    pieces.push(trimPiece(wall, 'skirting', 0, SKIRTING_HEIGHT, SKIRTING_PROUD, run, color));
    pieces.push(
      trimPiece(wall, 'cornice', top - CORNICE_HEIGHT, CORNICE_HEIGHT, CORNICE_PROUD, run, color),
    );

    if (height >= WAINSCOT_MIN_WALL_HEIGHT) pieces.push(...wainscot(wall, run, color));
  }

  for (const face of built.doorFaces) pieces.push(...casing(face, color));
  for (const face of built.windowFaces) pieces.push(...windowSurround(face, color));

  return pieces;
}
